import { detectModerationFromTags } from '@/application/moderation/moderation.utils';
import { getProfileLocalEditTtlMs } from '@/config/user';
import { db } from '@/database/franky/franky';
import { Logger } from '@/libs/logger/logger';
import { getTtlRetryDelayMs, getTtlUserMs } from '@/libs/runtime-config/runtime-config';
import type { Pubky } from '@/models/models.types';
import { ModerationModel } from '@/models/moderation/moderation';
import { type ModerationModelSchema, ModerationType } from '@/models/moderation/moderation.schema';
import type { NexusModelTuple } from '@/models/shared/base/tuple/baseTuple.type';
import { UserStreamModel } from '@/models/stream/user/userStream';
import type { UserStreamId } from '@/models/stream/user/userStream.types';
import { UserDetailsModel } from '@/models/user/details/userDetails';
import type { UserDetailsModelSchema } from '@/models/user/details/userDetails.schema';
import { getUserDetailsRejectionReason } from '@/models/user/details/userDetails.utils';
import { UserRelationshipsModel } from '@/models/user/relationships/userRelationships';
import { UserTtlModel } from '@/models/user/ttl/userTtl';
import type { TUserStreamUpsertParams } from '@/services/local/stream/users/users.types';
import { LocalTagCacheService, type TagPreviewGuard } from '@/services/local/tag/tag-cache';
import {
  NexusSocialGraphStatus,
  type NexusTag,
  type NexusUser,
  type NexusUserCounts,
  type NexusUserRelationship,
} from '@/services/nexus/nexus.types';
import { getNexusResponseStartedAt } from '@/services/nexus/nexus.utils';

/** Tag-cache guard plus the fetch stamp used to keep local follow writes. */
export type PersistUsersGuard = TagPreviewGuard & {
  fetchStartedAt?: number;
};

const KNOWN_SOCIAL_GRAPH_STATUSES = new Set<string>(Object.values(NexusSocialGraphStatus));

/**
 * Nexus decides the badge tiers; a value this build does not know (a new tier, a renamed
 * one) is treated as "no ranking" so the badge hides instead of rendering an empty pill.
 */
function toSocialGraphStatus(value: NexusUser['social_graph_status']): NexusSocialGraphStatus | null {
  return value && KNOWN_SOCIAL_GRAPH_STATUSES.has(value) ? value : null;
}

/**
 * Local Stream Users Service
 *
 * Simple service to manage user stream IDs in IndexedDB.
 * Only stores arrays of user IDs (Pubky), no user data.
 * Handles followers, following, friends, and other user stream types.
 */
export class LocalStreamUsersService {
  private constructor() {}

  /**
   * Save or update a stream of user IDs
   * @param streamId - Composite ID in format 'userId:streamType' (e.g., 'user-ABC:followers')
   */
  static async upsert({ streamId, stream }: TUserStreamUpsertParams): Promise<void> {
    await UserStreamModel.upsert(streamId, stream);
  }

  /**
   * Get a stream of user IDs by stream ID
   * @param streamId - Composite ID in format 'userId:streamType' (e.g., 'user-ABC:followers')
   */
  static async findById(streamId: string): Promise<{ stream: Pubky[] } | null> {
    return await UserStreamModel.findById(streamId);
  }

  /**
   * Delete a user stream from cache
   * @param streamId - Composite ID in format 'userId:streamType' (e.g., 'user-ABC:followers')
   */
  static async deleteById(streamId: string): Promise<void> {
    await UserStreamModel.deleteById(streamId);
  }

  /**
   * Prepend user ID(s) to a stream
   * Only adds users if not already present
   *
   * @param streamId - The stream to prepend to
   * @param userIds - The user ID(s) to prepend
   */
  static async prependToStream(streamId: UserStreamId, userIds: Pubky[]): Promise<void> {
    await UserStreamModel.prependItems(streamId, userIds);
  }

  /**
   * Remove user ID(s) from a stream
   *
   * @param streamId - The stream to remove from
   * @param userIds - The user ID(s) to remove
   */
  static async removeFromStream(streamId: UserStreamId, userIds: Pubky[]): Promise<void> {
    await UserStreamModel.removeItems(streamId, userIds);
  }

  /**
   * Find which users are not yet persisted in cache
   * Used to identify missing user data that needs to be fetched
   *
   * @param userIds - Array of user IDs to check
   * @param viewerId - When set, a missing relationship row is also a cache miss (#1803)
   * @returns Array of user IDs that are not persisted in cache
   */
  static async getNotPersistedUsersInCache(userIds: Pubky[], viewerId?: Pubky): Promise<Pubky[]> {
    const [details, relationships] = await Promise.all([
      UserDetailsModel.findByIdsPreserveOrder(userIds),
      viewerId ? UserRelationshipsModel.findByIds(userIds) : Promise.resolve([]),
    ]);
    const hydratedRelationships = new Set(relationships.map((row) => row.id));
    return userIds.filter((id, index) => details[index] === undefined || (viewerId && !hydratedRelationships.has(id)));
  }

  /**
   * Persist user data to normalized tables
   * Separates user details, counts, tags, relationships, and TTL records
   * Also detects and persists moderation status for flagged profiles
   *
   * Relationship rows (`following` / `followed_by`) are only meaningful relative to a viewer.
   * When the batch was fetched without a `viewerId`, Nexus returns a viewer-agnostic
   * relationship, so the row is skipped instead of caching "unknown" as "not following".
   * A missing row reads as a cache miss and triggers a viewer-aware fetch (#1803).
   * Details follow the same freshness rule as `LocalProfileService.upsertDetails`.
   *
   * @param users - Array of users from Nexus API
   * @param tagGuard - Tag-cache guard; `viewerId` is required to persist relationship rows.
   *   `fetchStartedAt` skips relationship rows whose user TTL was written at or after that
   *   time so a local follow/unfollow during the request is not overwritten.
   * @returns Array of user IDs (Pubky)
   */
  static async persistUsers(users: NexusUser[], tagGuard: PersistUsersGuard = {}): Promise<Pubky[]> {
    tagGuard = { ...tagGuard, validatedAt: tagGuard.validatedAt ?? getNexusResponseStartedAt(users) };
    if (tagGuard.isCurrent && !tagGuard.isCurrent()) return [];
    const userCounts: NexusModelTuple<NexusUserCounts>[] = [];
    const userRelationships: NexusModelTuple<NexusUserRelationship>[] = [];
    const userTags: NexusModelTuple<NexusTag[]>[] = [];
    const userDetails: UserDetailsModelSchema[] = [];
    const userModerations: ModerationModelSchema[] = [];
    const userTtl: NexusModelTuple<{ lastUpdatedAt: number }>[] = [];

    const userIds: Pubky[] = [];
    const now = Date.now();

    for (const user of users) {
      const userId = user.details.id;
      userIds.push(userId);
      userCounts.push([userId, user.counts]);
      userRelationships.push([userId, user.relationship]);
      userTags.push([userId, user.tags]);
      // The badge tier lives on the Nexus user view, not on `details`; it rides on the
      // details row so profile reads stay a single lookup. An absent field (older Nexus)
      // is stored as `null` so readers treat it as "no ranking" rather than "never fetched".
      userDetails.push({
        ...user.details,
        nexusIndexedAt: user.details.indexed_at,
        social_graph_status: toSocialGraphStatus(user.social_graph_status),
      });
      userTtl.push([userId, { lastUpdatedAt: now }]);

      // Detect moderation from user tags
      const isModerated = detectModerationFromTags(user.tags);
      if (isModerated) {
        userModerations.push({
          id: userId,
          type: ModerationType.PROFILE,
          is_blurred: true,
          created_at: Date.now(),
        });
      }
    }

    // Bulk save to normalized tables
    await Promise.all([
      this.persistDetailsRelationshipsAndTtl(userIds, userDetails, userRelationships, userTtl, tagGuard),
      LocalTagCacheService.savePreviews('user', userTags, tagGuard, userCounts),
      // Persist moderation records for flagged profiles
      userModerations.length > 0 ? ModerationModel.bulkSave(userModerations) : Promise.resolve(),
    ]);

    return userIds;
  }

  /**
   * Writes details, relationships and TTL in one transaction, reading before writing.
   *
   * A rejected details row is kept. Pending edits retry after the retry delay without
   * shortening a fresher TTL. Older revisions without a pending edit renew the normal TTL:
   * the cached row is already newer, so repeated short retries cannot improve it.
   * Guest / viewer-less payloads skip the relationship row (#1803). When `fetchStartedAt`
   * is set, a user TTL written at or after that stamp means a local follow landed during
   * the request — keep that row.
   */
  private static async persistDetailsRelationshipsAndTtl(
    userIds: Pubky[],
    userDetails: UserDetailsModelSchema[],
    userRelationships: NexusModelTuple<NexusUserRelationship>[],
    userTtl: NexusModelTuple<{ lastUpdatedAt: number }>[],
    tagGuard: PersistUsersGuard,
  ): Promise<void> {
    await db.transaction('rw', [UserDetailsModel.table, UserRelationshipsModel.table, UserTtlModel.table], async () => {
      const fetchStartedAt = tagGuard.viewerId ? tagGuard.fetchStartedAt : undefined;
      const [existingDetails, existingTtl] = await Promise.all([
        UserDetailsModel.findByIdsPreserveOrder(userIds),
        UserTtlModel.findByIds(userIds),
      ]);

      const now = Date.now();
      const pendingEditMs = getProfileLocalEditTtlMs();
      const pendingIds = new Set<Pubky>();
      const detailsToSave = userDetails.filter((details, index) => {
        const rejection = getUserDetailsRejectionReason({
          existing: existingDetails[index],
          incoming: details,
          responseStartedAt: tagGuard.validatedAt,
          now,
          pendingEditMs,
        });
        if (rejection === 'pending-local-edit') pendingIds.add(details.id);
        return rejection === null;
      });

      let relationshipsToSave = tagGuard.viewerId ? userRelationships : [];
      if (fetchStartedAt !== undefined) {
        const skipIds = new Set(existingTtl.filter((row) => row.lastUpdatedAt >= fetchStartedAt).map((row) => row.id));
        if (skipIds.size > 0) {
          Logger.debug('LocalStreamUsersService: Skipped relationship rows written since the fetch started', {
            ids: Array.from(skipIds).slice(0, 5),
            count: skipIds.size,
          });
          relationshipsToSave = relationshipsToSave.filter(([id]) => !skipIds.has(id));
        }
      }

      const retryAt = now - (getTtlUserMs() - getTtlRetryDelayMs());
      const ttlById = new Map(existingTtl.map((row) => [row.id, row.lastUpdatedAt]));
      const ttlToSave = userTtl.flatMap(([id, ttl]): NexusModelTuple<{ lastUpdatedAt: number }>[] => {
        if (!pendingIds.has(id)) return [[id, ttl]];
        return (ttlById.get(id) ?? -Infinity) < retryAt ? [[id, { lastUpdatedAt: retryAt }]] : [];
      });

      await Promise.all([
        detailsToSave.length > 0 ? UserDetailsModel.bulkSave(detailsToSave) : Promise.resolve(),
        relationshipsToSave.length > 0 ? UserRelationshipsModel.bulkSave(relationshipsToSave) : Promise.resolve(),
        UserTtlModel.bulkSave(ttlToSave),
      ]);
    });
  }
}
